import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["searchInput", "budgetInput"]

  connect() {
    console.log("ProjectListController connected")
    this.debouncedSearch = this.debounce(this.submitForm.bind(this), 300)
  }

  viewMilestone(event) {
    event.preventDefault()
    
    // Get the URL from the data attribute
    const url = event.currentTarget.dataset.projectListUrlParam
    
    if (url) {
      // Use simple navigation to avoid frame issues
      window.location.href = url
    } else {
      console.error('No URL found in data-project-list-url-param attribute')
    }
  }

  view(event) {
    event.preventDefault()
    
    // Get the URL from the data attribute
    const url = event.currentTarget.dataset.projectListUrlParam
    
    if (url) {
      // Use simple navigation to avoid frame issues
      window.location.href = url
    } else {
      console.error('No URL found in data-project-list-url-param attribute')
    }
  }

  preventSubmit(event) {
    // Prevent form submission - we handle search via AJAX
    event.preventDefault()
  }

  handleKeydown(event) {
    // Prevent form submission on Enter key
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  submitForm(event) {
    // Collect data from both search form and filter form
    const searchForm = this.element.querySelector('.drawer-content form')
    const filterForm = this.element.querySelector('.drawer-side form')
    
    if (searchForm) {
      console.log("Submitting combined search and filter data")
      
      // Close the drawer
      this.closeDrawer()
      
      // Create combined FormData
      const combinedData = new FormData()
      
      // Add search form data
      const searchFormData = new FormData(searchForm)
      for (let [key, value] of searchFormData.entries()) {
        combinedData.append(key, value)
      }
      
      // Add filter form data if it exists
      if (filterForm) {
        const filterFormData = new FormData(filterForm)
        for (let [key, value] of filterFormData.entries()) {
          combinedData.append(key, value)
        }
      }
      
      // Remove any existing page parameter when searching
      // since search results should start from page 1
      combinedData.delete('page')
      
      // Convert to URLSearchParams
      const params = new URLSearchParams(combinedData)
      
      // Use the current page's path to preserve route context
      const currentPath = window.location.pathname
      
      // Make a fetch request to get turbo_stream response
      fetch(`${currentPath}?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => response.text())
      .then(html => {
        // Use Turbo to process the turbo_stream response
        Turbo.renderStreamMessage(html)
      })
      .catch(error => {
        console.error('Search request failed:', error)
      })
    } else {
      console.error("Could not find search form to submit")
    }
  }

  clearFilters(event) {
    event.preventDefault()
    console.log("Clearing filters")
    
    // Find the filter form specifically
    const filterForm = event.target.closest('.drawer-side').querySelector('form')
    if (filterForm) {
      // Clear all filter checkboxes
      filterForm.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
        checkbox.checked = false
      })
      
      // Clear number inputs (like budget percentage)
      filterForm.querySelectorAll('input[type="number"]').forEach(numberInput => {
        numberInput.value = ''
        numberInput.disabled = true
      })
      
      // Also clear search input from the main form
      const searchInput = this.element.querySelector('input[name="query"]')
      if (searchInput) {
        searchInput.value = ''
      }
      
      // Submit the combined forms to apply the cleared filters
      this.submitForm(event)
    }
  }

  closeDrawer() {
    const drawerToggle = document.getElementById('project-filters-drawer')
    if (drawerToggle) {
      drawerToggle.checked = false
    }
  }

  filterProjects(event) {
    const searchTerm = event.target.value
    const form = event.target.closest('form')
    
    if (form) {
      // Only do client-side filtering if we're likely on page 1 with no existing search
      // This prevents confusion with pagination
      const isPaginated = document.querySelector('.pagination') !== null
      const hasExistingSearch = new URLSearchParams(window.location.search).get('query')
      
      if (!isPaginated && !hasExistingSearch && searchTerm.length > 0) {
        // For quick feedback, do client-side filtering on first page only
        this.clientSideFilter(searchTerm.toLowerCase())
      }
      
      // Always trigger server-side search with debounce for comprehensive results
      this.debouncedSearch()
    }
  }

  clientSideFilter(searchTerm) {
    const rows = document.querySelectorAll('tr[data-action="click->project-list#viewMilestone"]')
    
    rows.forEach(row => {
      // Try to find project name first, fallback to any text content for milestones
      const projectName = row.querySelector('.project-name')?.textContent?.toLowerCase() || ''
      const milestoneNameElement = row.querySelector('.font-bold')
      const milestoneName = milestoneNameElement?.textContent?.toLowerCase() || ''
      
      // Search in both project name and milestone/general content
      const searchableText = projectName || milestoneName || row.textContent.toLowerCase()
      
      if (searchableText.includes(searchTerm)) {
        row.classList.remove('hidden')
      } else {
        row.classList.add('hidden')
      }
    })

    // Show/hide the "no projects found" message
    const noProjectsMessage = document.querySelector('.text-center.py-8.text-gray-500')
    if (noProjectsMessage) {
      const visibleRows = Array.from(rows).filter(row => !row.classList.contains('hidden'))
      noProjectsMessage.classList.toggle('hidden', visibleRows.length > 0)
    }
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  toggleBudgetInput(event) {
    const checkbox = event.target
    const budgetInput = this.budgetInputTarget
    
    if (checkbox.checked) {
      budgetInput.disabled = false
      budgetInput.focus()
      // Set default value if empty
      if (!budgetInput.value) {
        budgetInput.value = '70'
      }
    } else {
      budgetInput.disabled = true
      budgetInput.value = ''
    }
  }
}